import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter } from "lucide-react";
import { VehicleCard } from "./VehicleCard";
import { VehicleModal } from "./VehicleModal";
import { useVehicles } from "@/hooks/useVehicles";
import CreateAction from "@/components/common/CreateAction";
import { Vehicle } from "@/types";

interface VehicleListProps {
  showAddButton?: boolean;
  userId?: string;
}

export const VehicleList: React.FC<VehicleListProps> = ({
  showAddButton = true,
  userId,
}) => {
  const { t } = useTranslation("pages");
  const navigate = useNavigate();
  const { data: vehiclesData, isLoading, error } = useVehicles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const vehicles = vehiclesData || [];

  // Filter vehicles based on search term and userId
  const filteredVehicles = vehicles.filter((vehicle) => {
    const matchesSearch =
      !searchTerm ||
      vehicle.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${vehicle.year} ${vehicle.maker} ${vehicle.model}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesUser = !userId || vehicle.userId === userId;

    return matchesSearch && matchesUser;
  });

  const handleCreate = () => {
    setIsModalOpen(true);
  };

  const handleView = (vehicle: Vehicle) => {
    navigate(`/vehicles/${vehicle.id}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (isLoading) {
    return (
 <div className="flex items-center justify-center h-64">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
 <div className="text-center text-red-600 p-4">
        {t("vehicles.errorLoading")}
      </div>
    );
  }

  return (
 <div className="space-y-4 sm:space-y-6">
      {/* Header */}
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
 <h1 className="page-title">
            {userId ? t("vehicles.myVehicles") : t("vehicles.allVehicles")}
          </h1>
 <p className="text-secondary-600 text-sm sm:text-base">
            {t("vehicles.vehicleCount", { count: filteredVehicles.length })}
          </p>
        </div>

        {showAddButton && (
          <CreateAction onClick={handleCreate} label={t("vehicles.addVehicle")} />
        )}
      </div>

      {/* Search and Filters */}
 <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
          <input
            type="text"
            placeholder={t("vehicles.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
 className="flex items-center justify-center px-4 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors text-sm sm:text-base"
        >
 <Filter className="h-4 w-4 mr-2" />
          {t("vehicles.filters")}
        </button>
      </div>

      {/* Filters (expandable) */}
      {showFilters && (
 <div className="bg-secondary-50 rounded-lg p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                {t("vehicles.sortBy")}
              </label>
 <select className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="created_desc">{t("vehicles.sortNewest")}</option>
                <option value="created_asc">{t("vehicles.sortOldest")}</option>
                <option value="name_asc">{t("vehicles.sortNameAZ")}</option>
                <option value="name_desc">{t("vehicles.sortNameZA")}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Grid */}
      {filteredVehicles.length === 0 ? (
 <div className="text-center py-8 sm:py-12 px-4">
 <div className="text-secondary-400 mb-4">
 <Plus className="h-10 w-10 sm:h-12 sm:w-12 mx-auto" />
          </div>
 <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {vehicles.length === 0
              ? t("vehicles.noVehicles")
              : t("vehicles.noVehiclesMatch")}
          </h3>
 <p className="text-secondary-600 mb-4 text-sm sm:text-base">
            {vehicles.length === 0
              ? t("vehicles.noVehiclesMessage")
              : t("vehicles.noVehiclesMatchMessage")}
          </p>
          {showAddButton && vehicles.length === 0 && (
            <button
              onClick={handleCreate}
 className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
 <Plus className="h-4 w-4 mr-2" />
              {t("vehicles.addFirstVehicle")}
            </button>
          )}
        </div>
      ) : (
 <div className="flex flex-col gap-2">
          {filteredVehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              onView={handleView}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <VehicleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode="create"
      />
    </div>
  );
};
